import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, roleOf, can } from "@/lib/roles";
import {
  LayoutDashboard, Building2, Inbox, FileText, GraduationCap,
  BarChart3, Users, ScrollText, Settings, LogOut, ShieldCheck, Menu, Gauge,
} from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import NotificationBell from "@/components/NotificationBell";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";
import { KeyRound, UserCircle } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const NAV = [
  { key: "dashboard", to: "/", label: "Dashboard", icon: LayoutDashboard },
  { key: "institutions", to: "/institutions", label: "Institutions", icon: Building2 },
  { key: "admissions", to: "/admissions", label: "Admissions", icon: Inbox },
  { key: "concept", to: "/concept-papers", label: "Concept Papers", icon: FileText },
  { key: "supervision", to: "/supervisions", label: "Supervision", icon: GraduationCap },
  { key: "oversight", to: "/oversight", label: "Oversight", icon: Gauge },
  { key: "analytics", to: "/analytics", label: "Analytics", icon: BarChart3 },
  { key: "users", to: "/users", label: "Users", icon: Users },
  { key: "audit", to: "/audit", label: "Audit Log", icon: ScrollText },
  { key: "settings", to: "/settings", label: "Settings", icon: Settings },
];

function Brand({ tenant }) {
  return (
    <div className="flex items-center gap-3">
      {tenant?.logo_url ? (
        <img src={tenant.logo_url} alt="logo" className="h-9 w-9 object-contain" />
      ) : (
        <div className="h-9 w-9 rounded-md brand-bg flex items-center justify-center text-white">
          <ShieldCheck className="h-5 w-5" />
        </div>
      )}
      <div className="leading-tight">
        <p className="font-heading font-bold text-sm text-foreground truncate max-w-[150px]">
          {tenant?.name || "Platform Console"}
        </p>
        <p className="text-[11px] text-muted-foreground">{tenant?.tagline || "Human-led AI supervision"}</p>
      </div>
    </div>
  );
}

function NavLinks({ items, loc, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
      {items.map(({ key, to, label, icon: Icon }) => {
        const active = to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(to);
        return (
          <Link
            key={key}
            to={to}
            onClick={onNavigate}
            data-testid={`nav-${key}`}
            className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
              active ? "brand-bg text-white font-medium" : "text-muted-foreground hover:bg-secondary hover:text-foreground"
            }`}
          >
            <Icon className="h-4 w-4" />
            {label}
          </Link>
        );
      })}
    </nav>
  );
}

export default function Layout({ children }) {
  const { user, tenant, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const items = NAV.filter((n) => can(user, n.key));
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");
  const avatarUrl = user?.avatar_file_id ? `${process.env.REACT_APP_BACKEND_URL}/api/profile/avatar/${user.avatar_file_id}` : null;

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-card" data-testid="sidebar">
        <div className="flex items-center px-5 h-16 border-b border-border"><Brand tenant={tenant} /></div>
        <NavLinks items={items} loc={loc} />
        <div className="px-3 py-3 border-t border-border text-[11px] text-muted-foreground">AI prepares · Humans decide</div>
      </aside>

      <div className="flex-1 flex flex-col overflow-hidden">
        <header className="h-16 border-b border-border bg-white/70 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6 sticky top-0 z-10">
          <div className="flex items-center gap-3">
            {/* Mobile menu */}
            <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
              <SheetTrigger className="md:hidden" data-testid="mobile-menu-btn">
                <Menu className="h-6 w-6 text-foreground" />
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0 bg-card">
                <div className="flex items-center px-5 h-16 border-b border-border"><Brand tenant={tenant} /></div>
                <NavLinks items={items} loc={loc} onNavigate={() => setSheetOpen(false)} />
              </SheetContent>
            </Sheet>
            <div>
              <p className="text-xs text-muted-foreground">{ROLE_LABELS[roleOf(user)]}</p>
              <p className="font-heading font-semibold text-foreground text-sm sm:text-base">{tenant?.name || "Platform Console"}</p>
            </div>
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
          <NotificationBell />
          <DropdownMenu>
            <DropdownMenuTrigger data-testid="user-menu" className="outline-none">
              <div className="flex items-center gap-2">
                <span className="hidden sm:block text-sm text-foreground">{user?.name}</span>
                <Avatar className="h-9 w-9 brand-bg">
                  {avatarUrl && <AvatarImage src={avatarUrl} alt={user?.name} />}
                  <AvatarFallback className="brand-bg text-white text-xs">{initials}</AvatarFallback>
                </Avatar>
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white">
              <DropdownMenuLabel>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-muted-foreground">{user?.email}</p>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="profile-btn" onClick={() => navigate("/profile")} className="cursor-pointer">
                <UserCircle className="h-4 w-4 mr-2" /> My profile
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="change-password-btn" onClick={() => setPwOpen(true)} className="cursor-pointer">
                <KeyRound className="h-4 w-4 mr-2" /> Change password
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="logout-btn" onClick={async () => { await logout(); navigate("/login"); }} className="text-destructive cursor-pointer">
                <LogOut className="h-4 w-4 mr-2" /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">{children}</main>
      </div>

      <ChangePasswordDialog
        open={pwOpen || !!user?.must_change_password}
        forced={!!user?.must_change_password}
        onClose={() => setPwOpen(false)}
      />
    </div>
  );
}
